import React from "react";
import { Play } from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const stories = [{
  title: "The Brave Little Tree",
  category: "Animation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570824/img/diverse_children_animated_story.png",
  duration: "5:20",
  link: "/StoryLibraryPage"
}, {
  title: "Dancing with Colors",
  category: "Movement",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570824/img/diverse_family_dancing_therapy.png",
  duration: "12:45",
  link: "/StoryLibraryPage"
}, {
  title: "Finding My Voice",
  category: "Testimonial",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570824/img/diverse_youth_testimonial_healing.png",
  duration: "8:15",
  link: "/StoryLibraryPage"
}];
export default function StoriesInMotion() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="stories-in-motion" ref={ref} className="bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[var(--secondary)]/30 rounded-full blur-3xl opacity-50" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-[var(--accent)]/20 rounded-full blur-3xl opacity-40" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className={cn("max-w-2xl mx-auto text-center mb-16 transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-[var(--primary)] mb-6 tracking-tight">
            Stories in Motion
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Experience the power of storytelling through our curated collection of
            videos, designed to inspire hope, healing, and connection.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {stories.map((story, i) => <div key={i} data-index={i} className={cn("group flex flex-col gap-4 transition-all duration-700 delay-[100ms]", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
          transitionDelay: `${150 * i}ms`
        }}>
              <Link to={story.link} className="block w-full rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <div className="relative w-full">
                  <AspectRatio ratio={16 / 9}>
                    <img src={story.image} alt={story.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" />
                    {}
                    <div className="absolute inset-0 bg-[var(--primary)]/40 transition-colors duration-300 group-hover:bg-[var(--primary)]/10" />

                    {}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100">
                      <div className="w-16 h-16 rounded-full border-2 border-[var(--ring)] bg-[var(--primary)]/20 backdrop-blur-sm flex items-center justify-center shadow-2xl">
                        <Play className="w-6 h-6 text-[var(--ring)] ml-1 fill-[var(--ring)]" />
                      </div>
                    </div>
                  </AspectRatio>
                </div>
              </Link>

              <div className="flex flex-col items-start gap-2 px-1">
                <h3 className="text-xl font-sans font-semibold text-[var(--foreground)] group-hover:text-[var(--primary)] transition-colors">
                  {story.title}
                </h3>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[var(--secondary)]/80 px-3 py-1 rounded-full font-medium">
                    {story.category}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {story.duration}
                  </span>
                </div>
              </div>
            </div>)}
        </div>

        <div className={cn("mt-16 text-center transition-all duration-700 delay-500", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <Link to="/StoryLibraryPage" className="inline-flex items-center justify-center px-8 py-3 text-sm font-medium transition-colors rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary)]/90 shadow-md hover:shadow-lg">
            View Full Library
          </Link>
        </div>
      </div>
    </section>;
}